import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Instagram, MessageCircle, ShoppingBag, ArrowUpRight, Tv, Printer, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LINKS } from "../config";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 7; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) continue;
      const hr12 = ((h + 11) % 12) + 1;
      slots.push(`${hr12}:${m === 0 ? "00" : "30"} ${h < 12 ? "AM" : "PM"}`);
    }
  }
  return slots;
})();

const inputCls =
  "w-full bg-transparent border-b border-line focus:border-gold focus:shadow-[0_1px_0_0_#B8862B] outline-none rounded-none py-4 text-ink placeholder:text-smoke/70 transition-all duration-300 font-body";

const CLOSE_MIN = 23 * 60;

const HOUR_SLOTS = (() => {
  const s = [];
  for (let h = 7; h <= 22; h++) {
    const hr12 = ((h + 11) % 12) + 1;
    s.push(`${hr12}:00 ${h < 12 ? "AM" : "PM"}`);
  }
  return s;
})();

const parseSlotMin = (s) => {
  const m = /^\s*(\d{1,2}):(\d{2})\s*([AP]M)\s*$/i.exec(s || "");
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + parseInt(m[2], 10);
};

const EMPTY = { name: "", contact: "", email: "", topic: "booking", message: "", time: "", party_size: 2, duration: "1 hour", purpose: "" };

export const Contact = () => {
  const { t, isAr } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [occupied, setOccupied] = useState([]);

  const isReservation = form.topic === "booking" || form.topic === "meeting";
  const isMeeting = form.topic === "meeting";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const durHours = parseInt(form.duration, 10) || 1;
  const maxGuests = isMeeting ? 8 : 12;

  const slotDisabled = (s) => {
    if (!isMeeting) return false;
    const start = parseSlotMin(s);
    if (start == null) return false;
    const end = start + durHours * 60;
    if (end > CLOSE_MIN) return true;
    return occupied.some((o) => start < o.end && o.start < end);
  };

  useEffect(() => {
    if (!isMeeting || !date) {
      setOccupied([]);
      return undefined;
    }
    let active = true;
    axios
      .get(`${API}/meeting-availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => { if (active) setOccupied(r.data.occupied || []); })
      .catch(() => { if (active) setOccupied([]); });
    return () => { active = false; };
  }, [isMeeting, date]);

  useEffect(() => {
    if (form.time && slotDisabled(form.time)) setForm((f) => ({ ...f, time: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.duration, occupied, isMeeting]);

  useEffect(() => {
    if (isMeeting && Number(form.party_size) > 8) setForm((f) => ({ ...f, party_size: 8 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeeting]);

  const submit = async (e) => {
    e.preventDefault();
    if (isReservation && (!date || !form.time)) {
      toast.error(t.contact.needDate);
      return;
    }
    setSending(true);
    try {
      const payload = {
        name: form.name,
        contact: form.contact,
        topic: form.topic,
        message: form.message,
        ...(form.email && { email: form.email }),
        ...(isReservation && {
          date: format(date, "yyyy-MM-dd"),
          time: form.time,
          party_size: Number(form.party_size),
        }),
        ...(isMeeting && { duration: form.duration, purpose: form.purpose }),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success(t.contact.success);
      setForm(EMPTY);
      setDate(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error(t.contact.slotTaken);
      else if (status === 429) toast.error(t.contact.tooMany);
      else toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const SOCIALS = [
    { label: "Instagram", handle: t.contact.socials.instagram, href: LINKS.instagram, icon: Instagram, testid: "contact-instagram-link" },
    { label: "WhatsApp", handle: t.contact.socials.whatsapp, href: LINKS.whatsapp, icon: MessageCircle, testid: "contact-whatsapp-link" },
    { label: "Talabat", handle: t.contact.socials.talabat, href: LINKS.talabat, icon: ShoppingBag, testid: "contact-talabat-link" },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-parchment border-t border-line" data-testid="contact-section">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto grid md:grid-cols-2 gap-10 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-4">{t.contact.kicker}</p>
          <h2 className="font-cinzel font-medium text-3xl md:text-5xl text-pine tracking-wide mb-10">
            {t.contact.titleA} <span className="font-display italic font-light text-gold-shimmer tracking-tight">{t.contact.titleB}</span>
          </h2>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <input
              required
              value={form.name}
              onChange={set("name")}
              placeholder={t.contact.name}
              className={inputCls}
              data-testid="contact-name-input"
            />
            <input
              required
              value={form.contact}
              onChange={set("contact")}
              placeholder={t.contact.contactField}
              className={inputCls}
              data-testid="contact-detail-input"
            />
            <select
              value={form.topic}
              onChange={set("topic")}
              className={`${inputCls} bg-parchment cursor-pointer`}
              data-testid="contact-topic-select"
            >
              {t.contact.topics.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            {isReservation && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-8"
                data-testid="reservation-fields"
              >
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.email}
                  className={inputCls}
                  data-testid="reservation-email-input"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="reservation-date-button"
                        className={`${inputCls} flex items-center justify-between text-start cursor-pointer ${date ? "" : "text-smoke/70"}`}
                      >
                        <span>{date ? format(date, "EEE, d MMM yyyy") : t.contact.date}</span>
                        <CalendarIcon size={16} className="text-gold shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-paper border-line" align="start" data-testid="reservation-calendar-popover">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          setCalOpen(false);
                        }}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <select
                    value={form.time}
                    onChange={set("time")}
                    className={`${inputCls} bg-parchment cursor-pointer ${form.time ? "" : "text-smoke/70"}`}
                    data-testid="reservation-time-select"
                  >
                    <option value="" disabled>
                      {t.contact.time}
                    </option>
                    {TIME_SLOTS.map((s) => (
                      <option key={s} value={s} disabled={slotDisabled(s)}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                  <select
                    value={form.party_size}
                    onChange={set("party_size")}
                    className={`${inputCls} bg-parchment cursor-pointer`}
                    data-testid="reservation-guests-select"
                  >
                    {[...Array(maxGuests)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {t.contact.guest(i + 1)}
                      </option>
                    ))}
                  </select>
                  {isMeeting && (
                    <select
                      value={form.duration}
                      onChange={set("duration")}
                      className={`${inputCls} bg-parchment cursor-pointer`}
                      data-testid="reservation-duration-select"
                    >
                      {t.contact.durations.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {isMeeting && date && (
                  <div data-testid="availability-grid">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <p className="text-xs uppercase tracking-[0.2em] text-smoke">{t.contact.availability.title}</p>
                      <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.15em] text-smoke">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-pine/70 inline-block" /> {t.contact.availability.open}</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-line inline-block" /> {t.contact.availability.booked}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                      {HOUR_SLOTS.map((s) => {
                        const dis = slotDisabled(s);
                        const sel = form.time === s;
                        return (
                          <button
                            type="button"
                            key={s}
                            disabled={dis}
                            onClick={() => setForm((f) => ({ ...f, time: s }))}
                            data-testid={`slot-${s.replace(/[:\s]/g, "")}`}
                            className={`text-[11px] py-2 px-1 border transition-colors duration-200 ${
                              sel
                                ? "bg-gold text-forest border-gold font-semibold"
                                : dis
                                ? "bg-line/40 text-smoke/40 border-line cursor-not-allowed line-through"
                                : "bg-pine/5 text-pine border-pine/30 hover:bg-pine/15 cursor-pointer"
                            }`}
                          >
                            {s.replace(":00 ", " ")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {isMeeting && (
                  <input
                    value={form.purpose}
                    onChange={set("purpose")}
                    placeholder={t.contact.purpose}
                    className={inputCls}
                    data-testid="reservation-purpose-input"
                  />
                )}
              </motion.div>
            )}

            <textarea
              required={!isReservation}
              rows={isReservation ? 3 : 4}
              value={form.message}
              onChange={set("message")}
              placeholder={isReservation ? t.contact.notes : t.contact.message}
              className={`${inputCls} resize-none`}
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="bg-pine text-paper text-xs uppercase tracking-[0.2em] px-10 py-4 rounded-none hover:bg-forest disabled:opacity-50 transition-colors duration-300"
            >
              {sending ? t.contact.sending : t.contact.submit}
            </button>
            {isReservation && form.email && (
              <p className="text-xs text-smoke mt-4" data-testid="confirm-note">{t.contact.confirmNote}</p>
            )}
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col justify-center"
        >
          {isMeeting && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-10 bg-forest text-paper p-6 sm:p-8 border border-gold/20"
              data-testid="meeting-room-card"
            >
              <p className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-3">{t.contact.room.kicker}</p>
              <p className="text-paper/80 text-sm leading-relaxed mb-6">{t.contact.room.desc}</p>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-3xl text-gold" dir="ltr">{t.contact.room.price}</span>
              </div>
              <p className="text-paper/70 text-xs leading-relaxed mb-6">{t.contact.room.priceNote}</p>
              <ul className="space-y-2 mb-6">
                {t.contact.room.points.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-paper/85">
                    <Check size={14} className="text-gold shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-6 mb-6 text-paper/70">
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
                  <Tv size={16} className="text-gold" /> TV
                </span>
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
                  <Printer size={16} className="text-gold" /> Printer
                </span>
              </div>
              <div className="border-t border-paper/15 pt-5">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gold/90 mb-3">{t.contact.room.tncTitle}</p>
                <ul className="space-y-2 list-disc ps-5 text-paper/60 text-xs leading-relaxed">
                  {t.contact.room.tnc.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
          <p className={`${isAr ? "font-display italic" : "font-arabic"} text-3xl text-gold/90 mb-10`}>{t.contact.sideTitle}</p>
          <div className="space-y-2">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.testid}
                className="flex items-center justify-between border-b border-line py-6 group hover:border-gold/60 transition-colors duration-300"
              >
                <div className="flex items-center gap-5">
                  <s.icon size={18} className="text-gold" />
                  <div>
                    <p className="text-pine font-display text-xl group-hover:text-gold transition-colors duration-300">{s.label}</p>
                    <p className="text-smoke text-sm">{s.handle}</p>
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className={`text-smoke group-hover:text-gold transition-all duration-300 ${
                    isAr ? "-scale-x-100 group-hover:-translate-x-1" : "group-hover:translate-x-1"
                  } group-hover:-translate-y-1`}
                />
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
